import { WarningFilled } from '@ant-design/icons'
import { GoalReviewStatus, type Home } from '@lmc/ts-common'
import { Due, Icon, IconType } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { ProgressRing, Spinner } from 'components/generic'
import { NAMESPACE } from 'locale'
import { goalsConstants } from 'locale/goals'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useGoalsStats } from '../hooks/useGoalsStats'
import { getProgressColor } from '../utils/progressColor'
import { HomeCard } from './index'

const GoalsCard = () => {
  const { t } = useTranslation(NAMESPACE.GOALS)

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const { data, isLoading } = useGoalsStats()
  const navigate = useNavigate()

  const navigateToGoalManagement = () => {
    navigate('reports/goals/goals-management/', {
      state: { reviewStatus: [GoalReviewStatus.overdue] }
    })
  }

  const navigateToGoalsDashboard = () => {
    navigate('reports/goals/dashboard/')
  }

  const footer = isLoading ? undefined : (
    <Footer>
      <AlignedGroup>
        <ProgressRing
          color={getProgressColor(data?.percentageWithActiveGoal)}
          radius={45}
          progress={data?.percentageWithActiveGoal || 0}
        />
        <StyledText level={2}>
          {t(goalsConstants.SU_ACTIVE_GOAL, {
            nominative: preferredPeopleNominative
          })}
        </StyledText>
      </AlignedGroup>

      {data?.overdueGoals ? (
        <ClickableAlignedGroup onClick={navigateToGoalManagement}>
          <StyledWarningFilled />
          <UnderlinedText weight="medium">
            {t(goalsConstants.NO_OVERDUE, {
              variable: data.overdueGoals
            })}
          </UnderlinedText>
        </ClickableAlignedGroup>
      ) : (
        <Button variant="text" onClick={navigateToGoalsDashboard}>
          {t(goalsConstants.GOALS_DASHBOARD)}
        </Button>
      )}
    </Footer>
  )

  return (
    <HomeCard title={t(goalsConstants.GOALS)} footer={footer}>
      {isLoading ? (
        <Spinner style={{ paddingTop: 40 }} />
      ) : (
        <StatsWrapper>
          <AlignedGroup>
            <Due />
            <Text level={1} weight="bold">
              {data?.residentsWithInProgressGoal || 0}
            </Text>
            <StyledText level={1}>
              {t(goalsConstants.SU_GOAL_IN_PROGRESS, {
                nominative: preferredPeopleNominative
              })}
            </StyledText>
          </AlignedGroup>
          <AlignedGroup>
            <Icon customIcon={IconType.ReportsGoals} />
            <Text level={1} weight="bold">
              {data?.goalsCompletedInLast30Days || 0}
            </Text>
            <StyledText level={1}>
              {t(goalsConstants.GOALS_COMPLETED_30_DAYS)}
            </StyledText>
          </AlignedGroup>
        </StatsWrapper>
      )}
    </HomeCard>
  )
}

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const AlignedGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`
const ClickableAlignedGroup = styled(AlignedGroup)`
  min-width: 120px;
  cursor: pointer;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color4};
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  ${({ theme }) => theme.mediaQueries.tabletDown} {
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
`

const StyledWarningFilled = styled(WarningFilled)`
  color: ${({ theme }) => theme.color.status.danger};
  font-size: 24px;
`

const UnderlinedText = styled(Text)`
  text-decoration: underline;
`

export default GoalsCard
